type TranslateFn = (key: string, opts?: Record<string, unknown>) => string

type CronFields = {
  second?: string
  minute: string
  hour: string
  dayOfMonth: string
  month: string
  dayOfWeek: string
}

const parseCron = (cron: string): CronFields | null => {
  const parts = cron.trim().split(/\s+/)

  if (parts.length === 5) {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts
    return { minute, hour, dayOfMonth, month, dayOfWeek }
  }

  if (parts.length === 6) {
    const [second, minute, hour, dayOfMonth, month, dayOfWeek] = parts
    return { second, minute, hour, dayOfMonth, month, dayOfWeek }
  }

  return null
}

const isWildcard = (field: string) => field === '*'

const stepValue = (field: string): number | null => {
  const match = /^\*\/(\d+)$/.exec(field)
  return match ? Number(match[1]) : null
}

const pad = (value: number) => value.toString().padStart(2, '0')

const dayName = (dayOfWeek: string, locale: string): string | null => {
  const dow = Number(dayOfWeek)
  if (Number.isNaN(dow) || dow < 0 || dow > 6) return null

  // 2023-01-01 was a Sunday (dow 0), so offsetting from there gives every weekday in UTC.
  const date = new Date(Date.UTC(2023, 0, 1 + dow))
  return date.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' })
}

export const describeCron = (cron: string, t: TranslateFn, locale: string): string => {
  if (!cron) return '-'

  const fields = parseCron(cron)
  if (!fields) return cron

  const { second, minute, hour, dayOfMonth, month, dayOfWeek } = fields
  const isEveryDay = isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)

  const secondStep = second ? stepValue(second) : null
  if (secondStep && isWildcard(minute) && isWildcard(hour) && isEveryDay) {
    return secondStep === 1 ? t('cron-every-second') : t('cron-every-x-seconds', { count: secondStep })
  }

  const minuteStep = stepValue(minute)
  if (minuteStep && isWildcard(hour) && isEveryDay) {
    return minuteStep === 1 ? t('cron-every-minute') : t('cron-every-x-minutes', { count: minuteStep })
  }

  const hourStep = stepValue(hour)
  if (hourStep && isEveryDay) {
    return hourStep === 1 ? t('cron-every-hour') : t('cron-every-x-hours', { count: hourStep })
  }

  if (isWildcard(minute) && isWildcard(hour) && isEveryDay) {
    return t('cron-every-minute')
  }

  const minuteNum = Number(minute)
  const hourNum = Number(hour)

  if (!Number.isNaN(minuteNum) && isWildcard(hour) && isEveryDay) {
    return t('cron-every-hour')
  }

  if (Number.isNaN(minuteNum) || Number.isNaN(hourNum)) {
    return cron
  }

  const time = `${pad(hourNum)}:${pad(minuteNum)}`

  if (isWildcard(dayOfWeek) && isWildcard(dayOfMonth) && isWildcard(month)) {
    return t('cron-daily-at', { time })
  }

  if (!isWildcard(dayOfWeek) && isWildcard(dayOfMonth)) {
    const day = dayName(dayOfWeek, locale)
    if (day) return t('cron-weekly-on', { day, time })
  }

  if (!isWildcard(dayOfMonth) && isWildcard(dayOfWeek)) {
    return t('cron-monthly-on', { day: dayOfMonth, time })
  }

  return cron
}
